import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { AiUsageInstanceDto, AiUsageStatisticDto } from "Infrastructure/Api/Api";
import { useMemo } from "react";

type Props = {
	instances: AiUsageInstanceDto[];
	year: number;
	isIncluded: (item: AiUsageStatisticDto) => boolean;
	hideEmptyInstances: boolean;
};

type InstanceRow = {
	deviceName: string;
	countsByMonth: number[];
	total: number;
};

const MONTH_LABELS = [
	"Led",
	"Úno",
	"Bře",
	"Dub",
	"Kvě",
	"Čvn",
	"Čvc",
	"Srp",
	"Zář",
	"Říj",
	"Lis",
	"Pro",
];

const totalCellSx = { fontWeight: 600, bgcolor: "grey.100" };

export const AiUsageTable: React.FunctionComponent<Props> = ({
	instances,
	year,
	isIncluded,
	hideEmptyInstances,
}) => {
	const now = new Date();
	const isFutureMonth = (monthIndex: number) =>
		year > now.getFullYear() ||
		(year === now.getFullYear() && monthIndex > now.getMonth());

	const { rows, totalsByMonth, grandTotal } = useMemo(() => {
		const totals = Array<number>(12).fill(0);

		const allRows: InstanceRow[] = instances.map((instance) => {
			const countsByMonth = Array<number>(12).fill(0);

			for (const item of instance.items) {
				if (item.year !== year || !isIncluded(item)) {
					continue;
				}
				countsByMonth[item.month - 1] += item.count;
				totals[item.month - 1] += item.count;
			}

			return {
				deviceName: instance.deviceName,
				countsByMonth,
				total: countsByMonth.reduce((sum, count) => sum + count, 0),
			};
		});

		const visibleRows = allRows
			.filter((row) => !hideEmptyInstances || row.total > 0)
			.sort((a, b) => b.total - a.total || a.deviceName.localeCompare(b.deviceName));

		return {
			rows: visibleRows,
			totalsByMonth: totals,
			grandTotal: visibleRows.reduce((sum, row) => sum + row.total, 0),
		};
	}, [instances, year, isIncluded, hideEmptyInstances]);

	if (rows.length === 0) {
		return (
			<Paper variant="outlined" sx={{ p: 3 }}>
				<Typography variant="body2" color="text.secondary">
					Pro rok {year} nejsou žádná data k zobrazení.
				</Typography>
			</Paper>
		);
	}

	const renderCount = (count: number, monthIndex: number, bold = false) => (
		<TableCell
			key={monthIndex}
			align="right"
			sx={{
				fontWeight: bold ? 600 : undefined,
				color: count === 0 ? "text.disabled" : undefined,
				opacity: isFutureMonth(monthIndex) ? 0.4 : undefined,
			}}
		>
			{count === 0 ? "–" : count.toLocaleString("cs-CZ")}
		</TableCell>
	);

	return (
		<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "70vh" }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 600 }}>Instance</TableCell>
						{MONTH_LABELS.map((label, monthIndex) => (
							<TableCell
								key={label}
								align="right"
								sx={{ fontWeight: 600, opacity: isFutureMonth(monthIndex) ? 0.4 : undefined }}
							>
								{label}
							</TableCell>
						))}
						<TableCell align="right" sx={totalCellSx}>
							Celkem
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.deviceName} hover>
							<TableCell sx={{ whiteSpace: "nowrap" }}>{row.deviceName}</TableCell>
							{row.countsByMonth.map((count, monthIndex) => renderCount(count, monthIndex))}
							<TableCell align="right" sx={totalCellSx}>
								{row.total.toLocaleString("cs-CZ")}
							</TableCell>
						</TableRow>
					))}
					<TableRow sx={{ "& td": { borderTop: 2, borderTopColor: "divider", borderBottom: 0 } }}>
						<TableCell sx={{ fontWeight: 600 }}>Celkem</TableCell>
						{totalsByMonth.map((count, monthIndex) => renderCount(count, monthIndex, true))}
						<TableCell align="right" sx={totalCellSx}>
							{grandTotal.toLocaleString("cs-CZ")}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};
